<script lang="ts" setup>
/**
 * @file TableCell コンポーネント
 * 行や列ごとの配置などの見た目やスタイルに関する部分を担い、
 * 最適化されたツールチップ表示を使用します。
 */

import OptimizedTooltipText from '@/corePagesModule/components/OptimizedTooltipText.vue';
import { computed, ref } from 'vue';

/**
 * EzCellコンポーネントのプロパティ
 * @property {boolean} forceRight - 内容を右寄せにするかどうか
 * @property {boolean} forceCenter - 内容を中央寄せにするかどうか
 * @property {boolean} isNumber - 数値セルかどうか
 * @property {boolean} isNegative - 負の値を表示するセルかどうか
 * @property {boolean} isHighlighted - ハイライト表示するかどうか
 * @property {number} colspan - セルが占める列数
 */
interface TableCellProps {
  forceRight?: boolean;
  forceCenter?: boolean;
  isNumber?: boolean;
  isNegative?: boolean;
  isHighlighted?: boolean;
  hasRightBorder?: boolean;
  hasBottomBorder?: boolean;
  colspan?: number;
  contentPadding?: number;
  noUseTooltip?: boolean;
  lineHeight?: number;
  sticky?: boolean | 'left' | 'right';
  stickyZIndex?: number;
}

/** 受け取った Props を定義 */
const props = withDefaults(defineProps<TableCellProps>(), {
  forceRight: false,
  forceCenter: false,
  isNumber: false,
  isNegative: false,
  isHighlighted: false,
  hasRightBorder: false,
  hasBottomBorder: true,
  colspan: 1,
  contentPadding: 16,
  lineHeight: 12,
  sticky: false,
  stickyZIndex: 1,
});

const tooltipRef = ref<InstanceType<typeof OptimizedTooltipText> | null>(null);

/**
 * セルのCSSクラスを計算
 */
const cellClasses = computed(() => {
  const classes = {};

  if (props.forceRight || props.isNumber) {
    classes['table__cell--right'] = true;
  }

  if (props.forceCenter) {
    classes['table__cell--center'] = true;
  }

  if (props.isNumber) {
    classes['table__cell--number'] = true;
  }

  if (props.isNegative) {
    classes['table__cell--negative'] = true;
  }

  if (props.isHighlighted) {
    classes['table__cell--highlighted'] = true;
  }

  return classes;
});

/**
 * 外側のdivのクラス
 */
const outerClasses = computed(() => {
  const classes = { ...cellClasses.value };

  if (props.hasRightBorder) {
    classes['table__cell--border--right'] = true;
  }
  if (props.hasBottomBorder) {
    classes['table__cell--border--bottom'] = true;
  }
  if (props.sticky === true || props.sticky === 'left') {
    classes['table__cell--sticky'] = true;
    classes['table__cell--sticky--left'] = true;
  } else if (props.sticky === 'right') {
    classes['table__cell--sticky'] = true;
    classes['table__cell--sticky--right'] = true;
  }

  return classes;
});

/**
 * Tooltipに値変更を通知する
 */
const notifyTooltipContentChange = () => tooltipRef.value?.notifyContentChange();

defineExpose({
  notifyTooltipContentChange,
});
</script>

<template>
  <div class="table__cell" :class="outerClasses">
    <div v-if="props.noUseTooltip" class="table__cell__content" :class="cellClasses">
      <slot />
    </div>
    <OptimizedTooltipText v-else ref="tooltipRef" class="table__cell__content" :class="cellClasses">
      <slot />
    </OptimizedTooltipText>
  </div>
</template>

<style lang="scss" scoped>
.table__cell {
  display: grid;
  align-items: center;

  background-color: $semanticWhite;

  &--right {
    text-align: right;
  }

  &--center {
    text-align: center;
  }

  &--number {
    text-align: right;
    font-feature-settings: 'tnum';
    font-variant-numeric: tabular-nums;
  }

  &--negative {
    color: $semanticError !important;
  }

  &--highlighted {
    background-color: $semanticPrimarySubdued;
  }

  &--border {
    &--right {
      border-right: solid 1px $surfaceSecondary;
    }
    &--bottom {
      border-bottom: 1px solid $borderDefault;
    }
  }

  &--sticky {
    position: sticky;
    z-index: v-bind(stickyZIndex);
    &--left {
      left: 0;
    }
    &--right {
      right: 0;
    }
  }

  &__content {
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 400;
    line-height: v-bind('`${lineHeight}px`');
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: v-bind('`0 ${props.contentPadding}px`');
    color: $textSecondary;
  }
}
</style>
